<template>
  <section class="payment-result">
    <h2>{{ title }}</h2>
    <p>{{ message }}</p>
    <div v-if="isQrPayment && paymentState === 'pending'" class="qr-payment">
      <p v-if="context.amount">支付金额：¥{{ context.amount }}</p>
      <template v-if="!qrExpired">
        <img :src="context.qrCodeUrl" alt="支付宝收款二维码" referrerpolicy="no-referrer" @load="imageError = false" @error="imageError = true">
        <p v-if="imageError">二维码图片加载失败，请检查网络后刷新页面。</p>
        <p>长按图片保存 → 打开支付宝扫一扫 → 从相册选择图片支付。</p>
        <p class="qr-expiry">二维码剩余有效时间：<span class="qr-remaining">{{ remainingSeconds }} 秒</span></p>
        <p>保存图片不代表支付成功。</p>
      </template>
      <p v-else>二维码已过期，请先查询支付结果，再返回订单处理。</p>
    </div>
    <button v-if="canQuery" :disabled="querying" @click="refresh">{{ querying ? '查询中' : isQrPayment ? '我已支付，查询结果' : '刷新支付结果' }}</button>
    <button v-if="canContinue" :disabled="continuing" @click="continuePayment">继续支付</button>
    <button :disabled="returning" @click="returnToOrder">{{ returning ? '正在返回' : '返回订单' }}</button>
  </section>
</template>

<script>
import { getFuxunH5PaymentStatus } from '../utils/api'
import { paymentReturnPath, replacePaymentRoute } from '../utils/paymentReturnPath'
import pay, { readFuxunPayment, clearFuxunPayment, updateFuxunOrderNo } from '../utils/pingpay'

const states = {
  pending: ['支付结果确认中', '请稍后刷新，或返回订单查看支付结果。'],
  paid: ['支付成功', '付款已确认，返回后将刷新订单及相关业务数据。'],
  failed: ['支付失败', '请返回订单查看可用的支付操作。'],
  closed: ['支付已关闭', '请返回订单查看订单状态。'],
  expired: ['订单已过期', '请返回订单重新确认。'],
  refunded: ['订单已退款', '请返回订单查看退款详情。']
}

export default {
  data() {
    return {
      context: null,
      now: Date.now(),
      clock: null,
      imageError: false,
      paymentState: 'pending',
      querying: false,
      continuing: false,
      returning: false,
      attempts: 0,
      timer: null,
      active: false,
      queryError: '',
      requestVersion: 0
    }
  },
  computed: {
    isQrPayment() { return this.context && this.context.paymentMethod === 'ALIPAY' },
    remainingSeconds() { return Math.max(0, Math.ceil(((this.context && this.context.expiresAt || 0) - this.now) / 1000)) },
    qrExpired() { return this.remainingSeconds === 0 },
    title() { return this.isQrPayment && this.paymentState === 'pending' ? '支付宝扫码支付' : states[this.paymentState][0] },
    message() { return this.queryError || states[this.paymentState][1] },
    canQuery() {
      return this.context && (this.context.orderNo || this.context.params && this.context.params.repairNo)
        && this.paymentState === 'pending'
    },
    canContinue() { return !this.isQrPayment && this.canQuery && !this.querying && this.attempts > 0 && !this.queryError }
  },
  mounted() {
    window.addEventListener('pagehide', this.onPageHide)
    window.addEventListener('pageshow', this.onPageShow)
    this.startQuerying()
  },
  activated() { this.startQuerying() },
  deactivated() { this.stopQuerying() },
  beforeDestroy() {
    window.removeEventListener('pagehide', this.onPageHide)
    window.removeEventListener('pageshow', this.onPageShow)
    this.stopQuerying()
  },
  methods: {
    onPageHide() { this.stopQuerying() },
    onPageShow(event) {
      if (event.persisted && !this.active && this.$route.path === '/fuxunPayResult') {
        this.stopQuerying()
        this.startQuerying()
      }
    },
    startQuerying() {
      if (this.active) return
      this.active = true
      this.context = readFuxunPayment()
      this.now = Date.now()
      if (this.isQrPayment) this.clock = setInterval(() => { this.now = Date.now() }, 1000)
      this.paymentState = 'pending'
      this.queryError = this.canQuery ? '' : '支付上下文已失效，请返回订单查看结果。'
      this.attempts = 0
      this.queryStatus()
    },
    stopQuerying() {
      this.active = false
      clearInterval(this.clock)
      this.clock = null
      this.requestVersion += 1
      this.querying = false
      clearTimeout(this.timer)
      this.timer = null
    },
    async queryStatus() {
      if (!this.active || !this.canQuery || this.querying) return
      this.querying = true
      const version = ++this.requestVersion
      this.attempts += 1
      try {
        const response = await getFuxunH5PaymentStatus({
          orderNo: this.context.orderNo,
          repairNo: this.context.params && this.context.params.repairNo || ''
        })
        if (!this.active || version !== this.requestVersion) return
        if (!response.res || response.res.code !== 10000 || !response.body) {
          this.queryError = response.res && response.res.msg || '支付结果尚未确认，请稍后查询。'
        } else {
          this.queryError = ''
          if (response.body.orderNo) {
            this.context.orderNo = response.body.orderNo
            this.context.params = { ...this.context.params, orderNo: response.body.orderNo }
            updateFuxunOrderNo(response.body.orderNo)
          }
          const state = response.body.paymentState
          this.paymentState = states[state] ? state : 'pending'
        }
      } catch (error) {
        if (this.active && version === this.requestVersion) this.queryError = '查询暂时失败，请刷新或稍后查看订单。'
      } finally {
        if (version === this.requestVersion) this.querying = false
      }
      if (this.active && this.canQuery && this.attempts < 10) {
        this.timer = setTimeout(() => this.queryStatus(), 3000)
      }
    },
    refresh() {
      clearTimeout(this.timer)
      this.attempts = 0
      this.queryStatus()
    },
    async continuePayment() {
      if (this.continuing || !this.canContinue) return
      this.continuing = true
      clearTimeout(this.timer)
      await pay(this.context.params, '')
      this.continuing = false
      this.refresh()
    },
    async returnToOrder() {
      if (this.returning) return
      this.returning = true
      const marketId = this.context && this.context.marketId || this.$route.query.marketId
      let target = this.context && this.context.returnPath || '/myOrder'
      const businessType = String(this.context && this.context.params && this.context.params.bussinessType || '')
      if (this.paymentState === 'paid') {
        if (['1', '2', '4'].includes(businessType)) target = '/myOrder'
        if (businessType === '3') target = '/bill'
      }
      let path = paymentReturnPath(target, marketId)
      if (path.split('?')[0] === '/billDetail' && !window.sessionStorage.getItem('billDetailString')) {
        path = paymentReturnPath('/bill', marketId)
      }
      this.stopQuerying()
      try {
        await replacePaymentRoute(this.$router, path)
        if (this.paymentState !== 'pending') clearFuxunPayment()
      } catch (error) {
        this.startQuerying()
        this.queryError = '暂时无法返回订单，请重试。'
      } finally { this.returning = false }
    }
  }
}
</script>

<style lang="less" scoped>
.payment-result {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 100px 40px;
  text-align: center;
  background: #fff;
  min-height: 100vh;
  h2 { font-size: 40px; margin-bottom: 30px; }
  p { font-size: 28px; line-height: 44px; margin-bottom: 50px; color: #666; white-space: normal; overflow-wrap: break-word; }
  button { box-sizing: border-box; display: block; width: 100%; padding: 24px; margin: 24px 0; border: 1px solid #ddd; border-radius: 8px; font-size: 28px; background: #fff; }
  button:disabled { color: #999; }
  .qr-payment {
    min-width: 0;
    p { margin-bottom: 24px; }
    img { display: block; width: 440px; max-width: 100%; height: auto; margin: 0 auto 30px; -webkit-touch-callout: default; -webkit-user-select: auto; user-select: auto; }
    .qr-expiry { margin-bottom: 16px; }
    .qr-remaining { display: inline-block; white-space: nowrap; }
  }
}
</style>
